import { Copy, PlusCircle, UserIcon, MinusCircle, } from "lucide-react";
import { useLocation } from "react-router-dom";

import { logout } from "@/auth/auth";
import { useAcceptInvitation, useOrganization } from "@/client/backend/organization.tsx";
import { useUser, useUpdateUser } from "@/client/backend/user";
import type { components } from "@/client/backend/zz_schema";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList } from "@/components/ui/breadcrumb.tsx";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast"
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ReloadIcon } from "@radix-ui/react-icons";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { useOrganizationTokens, useCreateToken, useRevokeToken } from "@/client/backend/token";
import {useCreateOrganization, useUpdateOrganization, useOrganizationMembers, useRemoveOrganizationMember, useSendSupportMessage, useGetBillingSession, useGetCheckoutSession, useSubscriptions, useInviteUser} from "@/client/backend/organization";
import { useSWRConfig } from 'swr';
import { Sidebar } from "@/components/layout/Sidebar";
import { handleApiError } from "@/utils/api";
import { formatErrorMessage } from "@/utils/format";
import { ZarazEvents } from "@/utils/zaraz-events";
import { usePlans } from "@/client/backend/plans";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Section = "profile" | "organization" | "billing" | "support" | "tokens";

export type SettingsLinkProps = {
  initialSection?: Section;
};

const PLAN_TYPE_ON_DEMAND = "OnDemand";
const PLAN_TYPE_MONTHLY = "Monthly";
const PLAN_TYPE_YEARLY = "Yearly";

const isActiveSubscription = (sub: components["schemas"]["Subscription"]) => {
  return !sub.cancelAt || new Date(sub.cancelAt) > new Date();
};

export function Settings() {
  const { toast } = useToast()
  const location = useLocation();
  const { initialSection } = (location.state as SettingsLinkProps) ?? {};
  const { trigger: acceptInvitation } = useAcceptInvitation();  

  const searchParams = new URLSearchParams(location.search);
  const checkoutCancelledPriceId = searchParams.get('checkout_cancelled_price_id');
  const checkoutSuccessPriceId = searchParams.get('checkout_success_price_id');

  useEffect(() => {
    if (checkoutCancelledPriceId) {
      window.zaraz.track(ZarazEvents.CHECKOUT_CANCELLED, { priceId: checkoutCancelledPriceId });
    }
    if (checkoutSuccessPriceId) {
      window.zaraz.track(ZarazEvents.CHECKOUT_SUCCESS, { priceId: checkoutSuccessPriceId });
    }
  }, [checkoutCancelledPriceId, checkoutSuccessPriceId]);

  const { user } = useUser();
  const [section, setSection] = useState<Section>(initialSection ?? "profile");
  const [organization, setOrganization] = useOrganization();

  // Check if current user should see restricted sections for specific org
  const shouldShowRestrictedSections = () => {
    if (organization === "loading" || user === "loading") return false;
    // staging - ncr voyix mirror org
    if (organization.id === "146f3a1f-0d60-4c59-a082-b98175500441") {
      return user.emailAddress === "karl@limbar.io";
    }
    // prod - ncr voyix mirror org
    if (organization.id === "0231c7e1-7f04-4c28-96cb-a8e4007c3b94") {
      return user.emailAddress === "karl@limbar.io" || user.emailAddress === "muvaf@limrun.com";
    }
    // prod - ncr voyix real org. Chris, Brian and Valentina allowed.
    if (organization.id === "8a57c87e-462f-4e90-b8b7-5a413261ff9a") {
      return user.emailAddress === "christopher.jones@ncrvoyix.com" || user.emailAddress === "brian.cleveland@ncrvoyix.com" || user.emailAddress === "valentina.bedoya@ncrvoyix.com";
    }
    return true; // Show all sections for other organizations
  };

  // Redirect to profile if user tries to access restricted sections without permission
  useEffect(() => {
    if (user !== "loading" && organization !== "loading") {
      const restrictedSections = ["organization", "billing", "tokens"];
      if (restrictedSections.includes(section) && !shouldShowRestrictedSections()) {
        setSection("profile");
      }
    }
  }, [user, organization, section]);
  const [billingPortalSessionLoading, setBillingPortalSessionLoading] = useState<boolean>(false);
  // Local state to edit Default Inactivity Timeout (minutes)
  const [defaultInactivityTimeout, setDefaultInactivityTimeout] = useState<number | "loading">("loading");
  useEffect(() => {
    if (organization !== "loading" && defaultInactivityTimeout === "loading") {
      setDefaultInactivityTimeout(organization.defaultInactivityTimeoutMinutes);
    }
  }, [organization, defaultInactivityTimeout]);

  // Local state for editable Concurrency Limit
  const [concurrencyLimit, setConcurrencyLimit] = useState<number | "loading">("loading");
  useEffect(() => {
    if (organization !== "loading" && concurrencyLimit === "loading") {
      setConcurrencyLimit(organization.concurrencyLimit);
    }
  }, [organization, concurrencyLimit]);

  // Local states for organization display name and description
  const [orgDisplayName, setOrgDisplayName] = useState<string | "loading">("loading");
  const [orgDescription, setOrgDescription] = useState<string | "loading">("loading");

  useEffect(() => {
    if (organization !== "loading") {
      if (orgDisplayName === "loading") {
        setOrgDisplayName(organization.displayName);
      }
      if (orgDescription === "loading") {
        setOrgDescription(organization.description ?? "");
      }
    }
  }, [organization, orgDisplayName, orgDescription]);
  // Use inactivity timeout directly from organization object

  const [selectedDefaultOrganization, setSelectedDefaultOrganization] = useState<
    components["schemas"]["Organization"] | "loading"
  >("loading");
  if (user !== "loading" && selectedDefaultOrganization === "loading") {
    setSelectedDefaultOrganization(user.defaultOrganization);
  }
  const [selectedUserDisplayName, setSelectedUserDisplayName] = useState<string | "loading">("loading");
  if (user !== "loading" && selectedUserDisplayName === "loading" && user.displayName) {
    setSelectedUserDisplayName(user.displayName);
  }

  const { trigger: updateUser } = useUpdateUser();
  const { trigger: updateOrganization, isMutating: isUpdatingOrganization } = useUpdateOrganization(
    organization === "loading" ? "loading" : organization.id
  );

  const { trigger: getBillingSession } = useGetBillingSession(
    organization === "loading" ? "loading" : organization.id,
    user !== "loading" ? user.emailAddress : undefined
  );
  const [supportMessage, setSupportMessage] = useState<string | undefined>(undefined);
  const [supportMessageLoading, setSupportMessageLoading] = useState<boolean>(false);
  const { trigger: sendSupportMessage } = useSendSupportMessage(
    organization === "loading" ? "loading" : organization.id
  );
  const [supportMessageSubmissionResult, setSupportMessageSubmissionResult] = useState<string | undefined>(undefined);

  // State for accepting invitations
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const [acceptingOrgId, setAcceptingOrgId] = useState<string | null>(null);

  const [dialogToken, setDialogToken] = useState<string>();

  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [copied, setCopied] = useState(false);

  const { tokens = [] } = useOrganizationTokens(
    organization === "loading" ? undefined : organization.id
  );
  const { trigger: createToken } = useCreateToken(
    organization === "loading" ? "loading" : organization.id
  );
  const { trigger: revokeToken } = useRevokeToken(
    organization === "loading" ? "loading" : organization.id
  );

  const formatDateLong = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const { trigger: createOrganization } = useCreateOrganization();

  const [isCreateOrgDialogOpen, setIsCreateOrgDialogOpen] = useState(false);
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  const { mutate } = useSWRConfig();

  const { data: members } = useOrganizationMembers(
    organization === "loading" ? "loading" : organization.id
  );

  const [emailToAddToOrg, setEmailToAddToOrg] = useState("");
  const [lastInvitedEmail, setLastInvitedEmail] = useState<string | undefined>(undefined);
  const { trigger: inviteUser } = useInviteUser(
    organization === "loading" ? "loading" : organization.id
  );

  // State and handler for inviting members
  const [isInviting, setIsInviting] = useState(false);

  const handleInviteMember = async () => {
    if (organization === "loading" || !emailToAddToOrg.trim()) return;
    setIsInviting(true);
    try {
      const result = await inviteUser({ email: emailToAddToOrg.trim() });
      if (handleApiError(result, toast, "Invitation sent successfully.")) {
        await mutate(`/organizations/${organization.id}/members`);
        setLastInvitedEmail(emailToAddToOrg.trim());
      }
    } catch (error) {
      toast({
        title: "Failed to send invitation",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  // Handler for accepting invitation
  const handleAcceptInvitation = async (organizationId: string) => {
    setIsAcceptingInvitation(true);
    setAcceptingOrgId(organizationId);
    try {
      const response = await acceptInvitation({ organizationId });

      if (response?.response.status === 200) {
        toast({
          title: "Invitation accepted!",
          description: "You have successfully joined the organization.",
        });
        
        // Refresh user data to get updated organizations
        if (user !== "loading") {
          await mutate(`/users/${user.name}/memberships`);
        }
      } else {
        throw new Error("Failed to accept invitation");
      }
    } catch (error) {
      toast({
        title: "Failed to accept invitation",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsAcceptingInvitation(false);
      setAcceptingOrgId(null);
    }
  };

  const { trigger: removeMember } = useRemoveOrganizationMember(
    organization === "loading" ? "loading" : organization.id
  );

  const { trigger: getCheckoutSession } = useGetCheckoutSession(
    organization === "loading" ? "loading" : organization.id
  );

  const { data: subscriptions } = useSubscriptions(
    organization === "loading" ? "loading" : organization.id
  );

  const { plans } = usePlans();

  const [pricingPeriod, setPricingPeriod] = useState<'monthly' | 'yearly'>('yearly');

  const getEffectiveMonthlyPrice = (plan: components["schemas"]["Plan"]) => {
    if (plan.planType === PLAN_TYPE_ON_DEMAND) return 0;
    if (plan.planType === 'enterprise') return Infinity;
    return pricingPeriod === 'yearly' && plan.yearlyPrice
      ? plan.yearlyPrice / 12
      : plan.monthlyPrice || 0;
  };

  const groupPlans = (plans: components["schemas"]["Plan"][]) => {
    const grouped = new Map<string, components["schemas"]["Plan"][]>();

    plans.forEach(plan => {
      const baseName = plan.name.replace(/(Monthly|Yearly)$/, '').trim();
      if (!grouped.has(baseName)) {
        grouped.set(baseName, []);
      }
      grouped.get(baseName)?.push(plan);
    });

    return Array.from(grouped.values());
  };

  const content = (section: Section) => {
    switch (section) {
      case "profile":
        return (
          <div className="grid gap-6">
            <Card x-chunk="dashboard-04-chunk-1">
              <CardHeader>
                <CardTitle>{user !== "loading" ? (user.displayName ?? user.name) : ""}'s Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <Label className="mb-2 block text-sm font-medium text-muted-foreground">Name</Label>
                <Input
                  defaultValue={selectedUserDisplayName !== "loading" ? selectedUserDisplayName : undefined}
                  placeholder={selectedUserDisplayName !== "loading" ? selectedUserDisplayName : "John"}
                  className="mb-4"
                  onChange={(e) => {
                    setSelectedUserDisplayName(e.target.value);
                  }}
                />
                <Label className="mb-2 block text-sm font-medium text-muted-foreground">Email</Label>
                <Input placeholder={user !== "loading" ? user.emailAddress : ""} disabled className="mb-4" />
                <Label className="mb-2 block text-sm font-medium text-muted-foreground">Default Organization</Label>
                <Select
                  onValueChange={(value) => {
                    const selected =
                      user !== "loading" ? user.organizations.find((org) => org.id === value) : undefined;
                    if (selected) {
                      setOrganization(selected);
                    }
                  }}
                >
                  <SelectTrigger className="md:max-w-[300px] md:min-w-[240px]">
                    <SelectValue
                      placeholder={
                        selectedDefaultOrganization !== "loading"
                          ? selectedDefaultOrganization.displayName
                          : "Loading..."
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Organizations</SelectLabel>
                      {(user !== "loading" ? user.organizations : []).map((org) => (
                        <SelectItem value={org.id} key={org.id}>
                          {org.displayName}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </CardContent>
              <CardFooter className="border-t px-6 py-4 flex gap-2">
                <Button
                  type="submit"
                  onClick={async () => {
                    const result = await updateUser({
                      displayName: selectedUserDisplayName !== "loading" ? selectedUserDisplayName : undefined,
                      defaultOrganizationId: selectedDefaultOrganization !== "loading" ? selectedDefaultOrganization.id : undefined,
                    });
                    handleApiError(result, toast, "Profile updated successfully.");
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={logout}
                >
                  Sign out
                </Button>
              </CardFooter>
            </Card>

            {/* Pending Invitations Card */}
            {user !== "loading" && user.invitedOrganizations && user.invitedOrganizations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Pending Invitations</CardTitle>
                  <CardDescription>
                    You have been invited to join {user.invitedOrganizations.length === 1 ? "an organization" : "these organizations"}.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {user.invitedOrganizations.map((org) => (
                      <div
                        key={org.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{org.displayName}</p>
                          {org.description && (
                            <p className="text-sm text-muted-foreground">{org.description}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAcceptInvitation(org.id)}
                          disabled={isAcceptingInvitation && acceptingOrgId === org.id}
                        >
                          {isAcceptingInvitation && acceptingOrgId === org.id && (
                            <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          {isAcceptingInvitation && acceptingOrgId === org.id ? "Joining..." : "Accept"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );
      case "organization":
        if (!shouldShowRestrictedSections()) {
          return (
            <div className="grid gap-6">
              <Card>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">You don't have access to this section.</p>
                </CardContent>
              </Card>
            </div>
          );
        }
        // Helper to save changes programmatically (button click or Enter key)
        const handleSaveOrganization = async () => {
          if (organization === "loading" || isUpdatingOrganization) return;
          const orgUpdate: components["schemas"]["OrganizationMutable"] = {
            concurrencyLimit: concurrencyLimit !== "loading" ? concurrencyLimit : undefined,
            displayName: orgDisplayName !== "loading" ? orgDisplayName : undefined,
            description: orgDescription !== "loading" ? orgDescription : undefined,
            defaultInactivityTimeoutMinutes: defaultInactivityTimeout !== "loading" ? defaultInactivityTimeout : undefined,
          };

          const result = await updateOrganization(orgUpdate);
          if (handleApiError(result, toast, "Organization updated.")) {
            if (result?.data) {
              setOrganization(result.data);
              setConcurrencyLimit(result.data.concurrencyLimit);
              setDefaultInactivityTimeout(result.data.defaultInactivityTimeoutMinutes);
              setOrgDisplayName(result.data.displayName);
              setOrgDescription(result.data.description ?? "");
            }
            if (user !== "loading") {
              await mutate(`/users/${user.name}/memberships`);
            }
          }
        };

        return (
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Organization Settings</CardTitle>
                <CardDescription>
                  Manage your organization settings and preferences.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="mb-2 block text-sm font-medium text-muted-foreground">Organization ID</Label>
                    <div className="flex gap-2">
                      <code className="flex-1 rounded-md border bg-muted px-3 py-2 font-mono text-sm">
                        {organization !== "loading" ? organization.tid : ""}
                      </code>
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                if (organization !== "loading") {
                                  navigator.clipboard.writeText(organization.tid);
                                  toast({
                                    description: "Organization ID copied to clipboard",
                                  });
                                }
                              }}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Copy ID</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={orgDisplayName !== "loading" ? orgDisplayName : ""}
                      placeholder="Acme Inc"
                      onChange={(e) => {
                        setOrgDisplayName(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={orgDescription !== "loading" ? orgDescription : ""}
                      placeholder=""
                      onChange={(e) => {
                        setOrgDescription(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="concurrencyLimit">Concurrency Limit</Label>
                    <Input
                      id="concurrencyLimit"
                      type="number"
                      min="0"
                      value={concurrencyLimit !== "loading" ? concurrencyLimit : ""}
                      placeholder="Number of instances"
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value);
                        if (!Number.isNaN(value)) {
                          setConcurrencyLimit(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      {organization !== "loading" ? (
                        <>
                          The maximum is {organization.maxConcurrencyLimit} for this organization. For more, please{" "}
                          <button
                            className="text-primary hover:underline"
                            onClick={() => setSection("support")}
                          >
                            submit a ticket
                          </button>
                          .
                        </>
                      ) : ""}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="inactivityTimeout">Default Inactivity Timeout (minutes)</Label>
                    <Input
                      id="inactivityTimeout"
                      type="number"
                      min="0"
                      value={defaultInactivityTimeout !== "loading" ? defaultInactivityTimeout : ""}
                      placeholder="Minutes before instance stops"
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value);
                        if (!Number.isNaN(value)) {
                          setDefaultInactivityTimeout(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Instances will automatically stop after this period of inactivity.
                    </p>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  disabled={organization === "loading" || isUpdatingOrganization}
                  onClick={handleSaveOrganization}
                >
                  {isUpdatingOrganization && (
                    <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isUpdatingOrganization ? "Saving" : "Save changes"}
                </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Members</CardTitle>
                <CardDescription>People who have access to this organization.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="inviteEmail">Add Member</Label>
                    <div className="flex gap-2">
                      <Input
                        id="inviteEmail"
                        placeholder="Add by email address..."
                        value={emailToAddToOrg}
                        onChange={(e) => {
                          setEmailToAddToOrg(e.target.value);
                          if (lastInvitedEmail !== undefined && e.target.value !== lastInvitedEmail) {
                            setLastInvitedEmail(undefined);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleInviteMember();
                          }
                        }}
                      />
                      {(() => {
                        const isInvited =
                          lastInvitedEmail !== undefined && lastInvitedEmail === emailToAddToOrg.trim();

                        return (
                          <Button
                            onClick={handleInviteMember}
                            disabled={isInviting || organization === "loading" || isInvited}
                          >
                            {isInviting && (
                              <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            {isInviting ? "Inviting" : isInvited ? "Invited!" : "Invite"}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>

                  {members?.data === undefined ? (
                    <div className="flex items-center justify-center p-4">
                      <ReloadIcon className="h-5 w-5 animate-spin" />
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {members.data.map((member) => (
                        <div
                          key={member.name}
                          className="flex items-center justify-between space-x-4 rounded-lg border p-4"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="rounded-full bg-secondary p-2">
                              <UserIcon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium leading-none">
                                {member.displayName || member.name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {member.emailAddress}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={async () => {
                              if (organization === "loading") return;
                              try {
                                const result = await removeMember({ emailAddress: member.emailAddress });
                                if (handleApiError(result, toast, "Member removed successfully.")) {
                                  await mutate(`/organizations/${organization.id}/members`);
                                }
                              } catch (error) {
                                toast({
                                  title: "Failed to remove member",
                                  description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                                  variant: "destructive",
                                });
                              }
                            }}
                          >
                            <MinusCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        );
      case "billing":
        if (!shouldShowRestrictedSections()) {
          return (
            <div className="grid gap-6">
              <Card>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">You don't have access to this section.</p>
                </CardContent>
              </Card>
            </div>
          );
        }
        return (
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Usage & Billing</CardTitle>
                <CardDescription>
                  Manage your subscription and billing settings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="mb-2 block text-sm font-medium text-muted-foreground">Budget</Label>
                    {organization !== "loading" && (
                      <div className="mb-2">
                        <Label className="block text-sm font-normal text-foreground">
                          {organization.creditMinutes.toLocaleString()} credit minutes
                        </Label>
                      </div>
                    )}
                    {subscriptions?.data && subscriptions.data.filter(sub => sub.status === "Active").length > 0 && (
                      <div>
                        <Label className="block text-sm font-normal text-foreground">
                          {subscriptions.data
                            .filter(sub => sub.status === "Active")
                            .reduce((total, sub) => total + (sub.remainingMinutes || 0), 0)
                            .toLocaleString()
                          } remaining monthly minutes
                        </Label>
                      </div>
                    )}
                  </div>
                  <Button
                  onClick={async () => {
                    if (organization === "loading") return;
                    window.zaraz.track(ZarazEvents.BILLING_PORTAL_CLICK);
                    setBillingPortalSessionLoading(true);
                    try {
                      const result = await getBillingSession();
                      if (handleApiError(result, toast)) {
                        const url = result?.data?.url ? new URL(result.data.url) : undefined;
                        if (url) {
                          if (user !== "loading") {
                            url.searchParams.set("prefilled_email", user.emailAddress);
                          }
                          window.location.href = url.toString();
                        }
                      }
                    } finally {
                      setBillingPortalSessionLoading(false);
                    }
                  }}
                  disabled={organization === "loading" || billingPortalSessionLoading}
                >
                  {billingPortalSessionLoading && (
                    <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {billingPortalSessionLoading ? "Opening..." : "Open Billing Portal"}
                </Button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Tabs
                defaultValue="yearly"
                value={pricingPeriod}
                onValueChange={(value) => setPricingPeriod(value as 'monthly' | 'yearly')}
                className="w-[300px] mx-auto"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="monthly" >Monthly</TabsTrigger>
                  <TabsTrigger value="yearly" >Yearly (30% off)</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="grid gap-6 md:grid-cols-3">
                {[...groupPlans(plans)]
                  .sort((a, b) => getEffectiveMonthlyPrice(a[0]) - getEffectiveMonthlyPrice(b[0]))
                  .map((planGroup) => {
                    const plan = planGroup.find(p => {
                      if (p.planType === PLAN_TYPE_ON_DEMAND || p.planType === 'enterprise') {
                        return true;
                      }
                      return pricingPeriod === 'yearly'
                        ? p.planType === PLAN_TYPE_YEARLY
                        : p.planType === PLAN_TYPE_MONTHLY;
                    }) || planGroup[0];

                    return (
                      <Card key={plan.id} className="flex flex-col">
                        <CardHeader>
                          <CardTitle>{plan.name}</CardTitle>
                          <CardDescription>
                            {plan.name === "Pay as you go"
                              ? 'Perfect for getting started and see how much you need.'
                              : plan.name === "Starter"
                                ? 'Best value for consistent usage, especially for CI/CD.'
                                : "Best for multiple teams developing and testing."}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="flex-1">
                          <div className="h-[88px]">
                            {plan.planType === 'enterprise' ? (
                              <>
                                <div className="text-3xl font-bold">Custom</div>
                                <div className="text-sm text-muted-foreground">contact for pricing</div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-baseline gap-1">
                                  <span className="text-3xl font-bold">
                                    ${plan.planType === 'enterprise' ? 'Custom' :
                                      pricingPeriod === 'yearly' ? (() => {
                                        const yearlyPlan = planGroup.find(p => p.planType === PLAN_TYPE_YEARLY);
                                        return yearlyPlan?.yearlyPrice
                                          ? (yearlyPlan.yearlyPrice / 12).toFixed(0)
                                          : plan.monthlyPrice?.toFixed(0) || '0';
                                      })()
                                      : plan.monthlyPrice?.toFixed(0) || '0'}
                                  </span>
                                  <span className="text-sm text-muted-foreground">/mo</span>
                                </div>
                                {pricingPeriod === 'yearly' && plan.planType !== PLAN_TYPE_ON_DEMAND && (() => {
                                  const yearlyPlan = planGroup.find(p => p.planType === PLAN_TYPE_YEARLY);
                                  const monthlyPlan = planGroup.find(p => p.planType === PLAN_TYPE_MONTHLY);
                                  return monthlyPlan && yearlyPlan ? (
                                    <div className="text-sm mt-2">
                                      Billed at <s className="opacity-50">${(monthlyPlan.monthlyPrice ?? 0) * 12}</s> ${yearlyPlan.yearlyPrice} /year
                                    </div>
                                  ) : null;
                                })()}
                              </>
                            )}
                          </div>
                          <div className="min-h-[120px] mb-4">
                            <ul className="space-y-2 text-sm">
                              {plan.planType === 'enterprise' ? (
                                <>
                                  <li>• White-label embedding</li>
                                  <li>• Priority support</li>
                                  <li>• Custom integrations</li>
                                </>
                              ) : plan.planType === PLAN_TYPE_ON_DEMAND ? (
                                <>
                                  <li>• ${plan.minutePrice?.toFixed(2)} per minute</li>
                                  <li>• Unlimited concurrency</li>
                                  <li>• No minimum commitment</li>
                                  <li>• Pay only for what you use</li>
                                </>
                              ) : plan.name === "Starter" ? (
                                <>
                                  <li>• {plan.monthlyMinutes?.toLocaleString()} minutes ({Math.floor((plan.monthlyMinutes || 0) / 60)} hours) monthly refill</li>
                                  <li>• Unlimited concurrency</li>
                                  <li>• App previews on pull requests <span className="text-muted-foreground">(soon)</span></li>
                                </>
                              ) : (
                                <>
                                  <li>• {plan.monthlyMinutes?.toLocaleString()} minutes ({Math.floor((plan.monthlyMinutes || 0) / 60)} hours) monthly refill</li>
                                  <li>• Unlimited concurrency</li>
                                  <li>• App previews on pull requests <span className="text-muted-foreground">(soon)</span></li>
                                  <li>• Integrate with your product <span className="text-muted-foreground">(soon)</span></li>
                                </>
                              )}
                            </ul>
                          </div>
                        </CardContent>
                        <div className="mt-auto px-6 pb-6">
                          {plan.planType === PLAN_TYPE_ON_DEMAND && subscriptions?.data?.some(sub =>
                            sub.stripePriceId === plan.stripePriceId && isActiveSubscription(sub)
                          ) && (
                            <div className="relative">
                              <div className="w-full rounded-t-lg bg-primary/10 pt-2 pb-3 text-sm font-medium text-muted-foreground text-center">
                                Enabled
                              </div>
                            </div>
                          )}
                          {plan.planType === PLAN_TYPE_MONTHLY && subscriptions?.data?.some(sub =>
                            sub.stripePriceId === plan.stripePriceId && isActiveSubscription(sub)
                          ) && (
                            <div className="relative">
                              <div className="w-full rounded-t-lg bg-primary/10 pt-2 pb-3 text-sm font-medium text-muted-foreground text-center">
                                {subscriptions?.data?.filter(sub =>
                                  sub.stripePriceId === plan.stripePriceId && isActiveSubscription(sub)
                                ).length} subscription{subscriptions?.data?.filter(sub =>
                                  sub.stripePriceId === plan.stripePriceId && isActiveSubscription(sub)
                                ).length === 1 ? '' : 's'}
                              </div>
                            </div>
                          )}
                          {plan.planType === 'enterprise' ? (
                            <Button
                              className="w-full"
                              onClick={() => {
                                window.zaraz.track(ZarazEvents.CHECKOUT_CUSTOM_CONTACT_US_CLICK);
                                setSection("support");
                              }}
                            >
                              Contact Us
                            </Button>
                          ) : (
                            <Button
                              className="w-full relative -mt-2"
                              onClick={async () => {
                                if (organization === "loading") return;

                                const hasActivePlan = subscriptions?.data?.some(sub =>
                                  sub.stripePriceId === plan.stripePriceId && isActiveSubscription(sub)
                                );

                                if (hasActivePlan && plan.planType === PLAN_TYPE_ON_DEMAND) {
                                  window.zaraz.track(ZarazEvents.BILLING_PORTAL_CLICK);
                                  setBillingPortalSessionLoading(true);
                                  const result = await getBillingSession();
                                  if (handleApiError(result, toast)) {
                                    const url = result?.data?.url ? new URL(result.data.url) : undefined;
                                    if (url) {
                                      if (user !== "loading") {
                                        url.searchParams.set("prefilled_email", user.emailAddress);
                                      }
                                      window.location.href = url.toString();
                                    }
                                  }
                                } else {
                                  window.zaraz.track(ZarazEvents.CHECKOUT_CLICK, { priceId: plan.stripePriceId });
                                  const result = await getCheckoutSession({
                                    priceId: plan.stripePriceId
                                  });
                                  if (handleApiError(result, toast)) {
                                    const url = result?.data?.url;
                                    if (url) {
                                      window.location.href = url;
                                    }
                                  }
                                }
                              }}
                              disabled={organization === "loading"}
                            >
                              {plan.planType === PLAN_TYPE_ON_DEMAND
                                ? (subscriptions?.data?.some(sub =>
                                    sub.stripePriceId === plan.stripePriceId && isActiveSubscription(sub)
                                  ) ? "Manage Plan" : "Enable")
                                : "Add"}
                            </Button>
                          )}
                        </div>
                      </Card>
                    );
                  })}
              </div>
            </div>
          </div>
        );
      case "support":
        return (
          <div className="grid gap-6">
            <Card x-chunk="dashboard-04-chunk-1">
              <CardHeader>
                <CardTitle>Let us help you</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid w-full gap-2">
                  <Textarea
                    onChange={(e) => {
                      setSupportMessage(e.target.value);
                    }}
                    placeholder="Type your message here."
                  />
                  {supportMessageSubmissionResult && (
                    <div className="text-sm text-muted-foreground">{supportMessageSubmissionResult}</div>
                  )}
                  <Button
                    disabled={supportMessageLoading}
                    onClick={async () => {
                      if (!supportMessage) {
                        return;
                      }
                      setSupportMessageLoading(true);
                      try {
                        const result = await sendSupportMessage({
                          message: supportMessage,
                        });
                        if (handleApiError(result, toast, "Message sent successfully.")) {
                          setSupportMessageSubmissionResult(
                            `Thanks! We'll reach out to you at ${user !== "loading" ? user.emailAddress : "your email address"}.`
                          );
                        }
                      } catch (error) {
                        toast({
                          title: "Failed to send message",
                          description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                          variant: "destructive",
                        });
                      } finally {
                        setSupportMessageLoading(false);
                      }
                    }}
                  >
                    {supportMessageLoading ? <ReloadIcon className="animate-spin h-5 w-5 mr-2" /> : ""}
                    Send message
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      case "tokens":
        if (!shouldShowRestrictedSections()) {
          return (
            <div className="grid gap-6">
              <Card>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">You don't have access to this section.</p>
                </CardContent>
              </Card>
            </div>
          );
        }
        return (
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>API Keys</CardTitle>
                <CardDescription>
                  Create and manage API keys for programmatic access to {organization !== "loading" ? organization.displayName : ""}.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Dialog open={isDialogOpen} onOpenChange={(open) => {
                    if (!open) {
                      setDialogToken(undefined);
                    }
                    setIsDialogOpen(open);
                  }}>
                    <DialogTrigger asChild>
                      <Button onClick={() => {
                        setDialogToken(undefined);
                        setIsDialogOpen(true);
                      }}>Create Token</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create API Key</DialogTitle>
                        <DialogDescription>
                          {dialogToken
                            ? <>You can use <code>LIM_API_KEY</code> environment variable for lim CLI to use this token.</>
                            : "Create a new API Key for programmatic access."
                          }
                        </DialogDescription>
                      </DialogHeader>
                      {dialogToken ? (
                        <div>
                          <div className="p-2 bg-muted rounded-md max-h-32 overflow-auto flex justify-between items-start gap-2">
                            <pre className="flex-1">
                              <code className="whitespace-pre-wrap break-all text-xs">{dialogToken}</code>
                            </pre>
                            <TooltipProvider>
                              <Tooltip delayDuration={0} open={copied}>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 shrink-0"
                                    onClick={() => {
                                      navigator.clipboard.writeText(dialogToken);
                                      setCopied(true);
                                      setTimeout(() => setCopied(false), 1000);
                                    }}
                                  >
                                    <Copy className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Copied!
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <DialogFooter className="mt-4">
                            <Button onClick={() => setIsDialogOpen(false)}>Done</Button>
                          </DialogFooter>
                        </div>
                      ) : (
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          if (organization === "loading") return;

                          setIsCreatingToken(true);
                          const formData = new FormData(e.currentTarget);
                          const tokenCreate = {
                            description: formData.get("description") as string,
                            expirationMonths: Number.parseInt(formData.get("expiration") as string),
                          };

                          try {
                            const result = await createToken(tokenCreate);
                            if (handleApiError(result, toast)) {
                              setDialogToken(result?.data?.token);
                            }
                          } catch (error) {
                            toast({
                              title: "Failed to create token",
                              description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                              variant: "destructive",
                            });
                          } finally {
                            setIsCreatingToken(false);
                          }
                        }}>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="description">Description</Label>
                              <Input
                                id="description"
                                name="description"
                                placeholder="Token description (optional)"
                                disabled={isCreatingToken}
                              />
                            </div>
                            <div>
                              <Label htmlFor="expiration">Expiration</Label>
                              <Select
                                name="expiration"
                                defaultValue="3"
                                disabled={isCreatingToken}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select expiration" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="3">3 months</SelectItem>
                                  <SelectItem value="12">1 year</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <DialogFooter>
                              <Button
                                type="submit"
                                disabled={isCreatingToken}
                              >
                                {isCreatingToken && (
                                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Create Token
                              </Button>
                            </DialogFooter>
                          </div>
                        </form>
                      )}
                    </DialogContent>
                  </Dialog>

                  <div className="space-y-4">
                    {tokens === "loading" ? (
                      <div className="flex items-center justify-center p-4">
                        <ReloadIcon className="h-5 w-5 animate-spin" />
                      </div>
                    ) : (
                      tokens.map((token) => (
                        <div key={token.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <h4 className="font-medium">{token.id}</h4>
                            {token.description && (
                              <p className="text-sm text-muted-foreground">{token.description}</p>
                            )}
                            <p className="text-sm text-muted-foreground">
                              {token.revokedAt ? "Revoked" : new Date(token.expiresAt) > new Date() ? "Expires" : "Expired"} at{" "}
                              {formatDateLong(token.revokedAt || token.expiresAt)}
                            </p>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={!!token.revokedAt || new Date(token.expiresAt) <= new Date()}
                            onClick={async () => {
                              if (organization === "loading") return;
                              const result = await revokeToken(token.id);
                              if (handleApiError(result, toast, "Token revoked successfully.")) {
                                await mutate(`/organizations/${organization.id}/tokens`);
                              }
                            }}
                          >
                            {token.revokedAt ? "Revoked" : new Date(token.expiresAt) <= new Date() ? "Expired" : "Delete"}
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
    }
  };
  return (
    <div className="flex min-h-screen w-full flex-col bg-muted/40">
      <Sidebar />
      <div className="flex flex-col sm:gap-4 sm:py-4 sm:pl-14">
        <header className="sticky top-0 flex h-14 items-center gap-4 border-b bg-background px-4 sm:static sm:h-auto sm:border-0 sm:bg-transparent sm:px-6">
          <Breadcrumb className="hidden md:flex">
            <BreadcrumbList>
              <BreadcrumbItem className="flex items-center gap-2">
                <Select
                  onValueChange={(value) => {
                    const selected =
                      user !== "loading" ? user.organizations.find((org) => org.id === value) : undefined;
                    if (selected) {
                      setOrganization(selected);
                    }
                  }}
                >
                  <SelectTrigger className="md:max-w-[300px] md:min-w-[240px]">
                    <SelectValue placeholder={organization === "loading" ? "Loading..." : organization.displayName} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Organizations</SelectLabel>
                      {(user !== "loading" ? user.organizations : []).map((org) => (
                        <SelectItem value={org.id} key={org.id}>
                          {org.displayName ?? org.id}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>

                <Dialog open={isCreateOrgDialogOpen} onOpenChange={setIsCreateOrgDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <PlusCircle className="h-4 w-4" />
                      <span className="sr-only">Create Organization</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Organization</DialogTitle>
                      <DialogDescription>
                        Create a new organization to manage your resources.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      setIsCreatingOrg(true);

                      const formData = new FormData(e.currentTarget);
                      const orgCreate = {
                        displayName: formData.get("name") as string,
                        description: formData.get("description") as string,
                      };

                      try {
                        const result = await createOrganization(orgCreate);
                        if (handleApiError(result, toast, "Organization created successfully.")) {
                          if (result?.data) {
                            setOrganization(result.data);
                            setIsCreateOrgDialogOpen(false);
                            if (user !== "loading") {
                              await mutate(`/users/${user.name}/memberships`);
                            }
                          }
                        }
                      } catch (error) {
                        toast({
                          title: "Failed to create organization",
                          description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                          variant: "destructive",
                        });
                      } finally {
                        setIsCreatingOrg(false);
                      }
                    }}>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name</Label>
                          <Input
                            id="name"
                            name="name"
                            placeholder="Acme Inc"
                            required
                            disabled={isCreatingOrg}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="description">Description</Label>
                          <Input
                            id="description"
                            name="description"
                            disabled={isCreatingOrg}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          type="submit"
                          disabled={isCreatingOrg}
                        >
                          {isCreatingOrg && (
                            <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Create
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

        </header>
        <main className="flex min-h-[calc(100vh_-_theme(spacing.16))] flex-1 flex-col gap-4 bg-muted/40 p-4 md:gap-8 md:p-10">
          <div className="mx-auto grid w-full max-w-6xl gap-2">
            <h1 className="text-3xl font-semibold">Settings</h1>
          </div>
          <div className="mx-auto grid w-full max-w-6xl items-start gap-6 md:grid-cols-[180px_1fr] lg:grid-cols-[250px_1fr]">
            <nav className="grid gap-4 text-sm text-muted-foreground" x-chunk="dashboard-04-chunk-0">
              <Button
                variant="link"
                onClick={() => setSection("profile")}
                className={`-justify-center px-0 ${section === "profile" ? "font-semibold text-primary" : "opacity-50"}`}
              >
                Profile
              </Button>
              {shouldShowRestrictedSections() && (
                <Button
                  variant="link"
                  onClick={() => setSection("organization")}
                  className={`-justify-center px-0 ${section === "organization" ? "font-semibold text-primary" : "opacity-50"}`}
                >
                  Organization
                </Button>
              )}
              {shouldShowRestrictedSections() && (
                <Button
                    variant="link"
                    onClick={() => setSection("tokens")}
                    className={`-justify-center px-0 ${section === "tokens" ? "font-semibold text-primary" : "opacity-50"}`}
                >
                  API Keys
                </Button>
              )}
              {shouldShowRestrictedSections() && (
                <Button
                  variant="link"
                  onClick={() => setSection("billing")}
                  className={`-justify-center px-0 ${section === "billing" ? "font-semibold text-primary" : "opacity-50"}`}
                >
                  Billing
                </Button>
              )}
              <Button
                variant="link"
                onClick={() => setSection("support")}
                className={`-justify-center px-0 ${section === "support" ? "font-semibold text-primary" : "opacity-50"}`}
              >
                Support
              </Button>
            </nav>
            {content(section)}
          </div>
        </main>
      </div>
    </div>
  );
}
